import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Power, ShieldCheck, ChevronLeft, ChevronRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const FLAG = {
  none: "text-[#6E7582] border-white/10",
  "pii-redacted": "text-[#E9C46A] border-[#E9C46A]/30 bg-[#E9C46A]/10",
  "off-corpus": "text-[#3A86FF] border-[#3A86FF]/30 bg-[#3A86FF]/10",
};

const FLAGS = [["", "All flags"], ["none", "Clean"], ["pii-redacted", "PII redacted"], ["off-corpus", "Outside the memory"]];
const MODES = [["", "Voice & text"], ["voice", "Voice only"], ["text", "Text only"]];
const PAGE = 25;

const ALWAYS_ON = [
  "PII redaction before anything is stored",
  "Questions outside this Lab's memory are flagged, never answered from elsewhere",
  "Grounding-only system prompt",
  "90-day retention on captured turns",
];

export default function Guardrails() {
  const [s, setS] = useState(null);
  const [app, setApp] = useState(null);
  const [rtModels, setRtModels] = useState([]);
  const [log, setLog] = useState(null);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [flag, setFlag] = useState("");
  const [mode, setMode] = useState("");

  const fetchLog = useCallback(() => {
    api.moderation({ limit: PAGE, skip, flag: flag || undefined, mode: mode || undefined })
      .then((r) => { setLog(r.items); setTotal(r.total); })
      .catch(() => setLog([]));
  }, [skip, flag, mode]);

  useEffect(() => { api.settings().then(setS); }, []);
  useEffect(() => { api.adminApp().then(setApp).catch(() => {}); }, []);
  useEffect(() => { api.models().then((m) => setRtModels(m.realtime || [])).catch(() => {}); }, []);
  useEffect(() => { fetchLog(); }, [fetchLog]);
  useEffect(() => { setSkip(0); }, [flag, mode]);

  const save = async (patch, msg) => {
    try { setS(await api.patchSettings(patch)); if (msg) toast(msg); } catch (e) { toast.error(errMsg(e)); }
  };

  const saveApp = async (patch, msg) => {
    try { setApp(await api.patchApp(patch)); if (msg) toast(msg); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!s) return <p className="text-sm text-[#6E7582]">Loading…</p>;

  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + PAGE, total);

  return (
    <div className="space-y-5" data-testid="guardrails">
      <header>
        <p className="eyebrow">Safety</p>
        <h2 className="mt-1 font-display text-3xl font-semibold tracking-tight">Stop it, and see what it said</h2>
        <p className="mt-1 text-sm text-[#6E7582]">Spend caps, models, voice and limits now live in the Studio.</p>
      </header>

      <section className={`flex flex-wrap items-center gap-5 rounded-2xl border p-6 transition-colors ${s.kill_switch ? "border-[#E63946]/50 bg-[#E63946]/10" : "border-white/[0.06] bg-[#1A1D21]"}`} data-testid="kill-switch-panel">
        <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${s.kill_switch ? "bg-[#E63946] text-white" : "bg-white/[0.04] text-[#A1A8B3]"}`}><Power size={24} /></div>
        <div className="min-w-[220px] flex-1">
          <h3 className="font-display text-lg font-medium">Kill switch</h3>
          <p className="text-sm text-[#A1A8B3]">{s.kill_switch ? "Griot is disabled. Participants see the 'resting' message." : "Instantly pause every participant conversation in this Lab. Takes effect on the next request."}</p>
        </div>
        <button data-testid="kill-switch-btn" onClick={() => save({ kill_switch: !s.kill_switch }, s.kill_switch ? "Griot re-enabled" : "Griot disabled for all participants")} className={`rounded-full px-5 py-2.5 text-sm font-semibold ${s.kill_switch ? "bg-[#F3EFE6] text-[#121417]" : "border border-[#E63946]/50 text-[#E63946] hover:bg-[#E63946]/10"}`}>
          {s.kill_switch ? "Re-enable Griot" : "Disable Griot"}
        </button>
      </section>

      <section className="rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-6" data-testid="solo-panel">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="min-w-[260px] flex-1">
            <h3 className="font-display text-lg font-medium">Single Griot mode</h3>
            <p className="mt-1 text-sm text-[#A1A8B3]">
              {app?.solo_mode
                ? `Visitors land straight in conversation with ${app.solo_lab_name || app.solo_lab}. No Lab picker, and no other Lab can be reached — even with a direct link.`
                : "Visitors choose a Lab first. Every open Lab is reachable."}
            </p>
          </div>
          <button
            data-testid="solo-mode-btn"
            onClick={() => saveApp({ solo_mode: !app?.solo_mode }, app?.solo_mode ? "All Labs are reachable again" : "One Griot is alive")}
            disabled={!app}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold ${app?.solo_mode ? "border border-white/15 text-[#D8D2C4] hover:bg-white/5" : "bg-[#E07A5F] text-[#121417]"}`}
          >
            {app?.solo_mode ? "Open all Labs" : "Turn on single mode"}
          </button>
        </div>
        {app?.solo_mode && (
          <div className="mt-5 grid gap-4 border-t border-white/[0.06] pt-5 sm:grid-cols-2">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#6E7582]">the living Griot</span>
              <input
                data-testid="solo-lab-input"
                defaultValue={app.solo_lab}
                onBlur={(e) => e.target.value.trim() !== app.solo_lab && saveApp({ solo_lab: e.target.value.trim() }, "Living Griot changed")}
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#121417] px-3 py-2 text-sm text-[#F3EFE6] focus:border-[#E07A5F] focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#6E7582]">flowing voice model</span>
              <select
                data-testid="realtime-model-select"
                value={s.realtime_model}
                onChange={(e) => save({ realtime_model: e.target.value }, "Flowing voice model saved")}
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#121417] px-3 py-2 text-sm text-[#F3EFE6] focus:border-[#E07A5F] focus:outline-none"
              >
                {(rtModels.length ? rtModels : [s.realtime_model]).map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </label>
            <label className="flex items-center justify-between gap-4 sm:col-span-2">
              <span className="text-sm text-[#D8D2C4]">
                Speech-to-speech flowing conversation
                <span className="mt-0.5 block text-xs text-[#6E7582]">Off falls back to transcribe → answer → speak, which adds about two seconds before Griot talks.</span>
              </span>
              <button
                data-testid="realtime-toggle-btn"
                onClick={() => save({ realtime: !s.realtime }, s.realtime ? "Flowing is back on the slower loop" : "Flowing runs on the realtime voice")}
                className={`shrink-0 rounded-full px-4 py-2 text-xs font-semibold ${s.realtime ? "bg-[#2EC4B6] text-[#121417]" : "border border-white/15 text-[#A1A8B3]"}`}
              >
                {s.realtime ? "on" : "off"}
              </button>
            </label>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-white/[0.06] bg-[#1A1D21] p-6" data-testid="always-on-panel">
        <h3 className="font-display text-base font-medium">Always on</h3>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {ALWAYS_ON.map((l) => (
            <li key={l} className="flex items-start gap-2.5 text-sm text-[#D8D2C4]"><ShieldCheck size={15} className="mt-0.5 shrink-0 text-[#2EC4B6]" /> {l}</li>
          ))}
        </ul>
      </section>

      <section className="rounded-2xl border border-white/[0.06] bg-[#1A1D21]" data-testid="moderation-log">
        <div className="flex flex-wrap items-end justify-between gap-3 p-5 pb-4">
          <div>
            <h3 className="font-display text-base font-medium">Moderation log</h3>
            <p className="text-xs text-[#6E7582]">Anonymised, PII-redacted turns — only captured when the participant opted in.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="moderation-flag-filter" value={flag} onChange={(e) => setFlag(e.target.value)} className="rounded-full border border-white/10 bg-[#121417] px-3 py-1.5 text-xs text-[#F3EFE6] focus:border-[#E07A5F] focus:outline-none">
              {FLAGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select data-testid="moderation-mode-filter" value={mode} onChange={(e) => setMode(e.target.value)} className="rounded-full border border-white/10 bg-[#121417] px-3 py-1.5 text-xs text-[#F3EFE6] focus:border-[#E07A5F] focus:outline-none">
              {MODES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <span className="font-mono text-[11px] text-[#6E7582]" data-testid="moderation-count">{total} turns · 90d</span>
          </div>
        </div>

        {log === null && <p className="px-5 pb-6 text-sm text-[#6E7582]">Loading…</p>}
        {log?.length === 0 && <p className="px-5 pb-6 text-sm text-[#6E7582]" data-testid="moderation-empty">No captured turns match this filter.</p>}

        <ul className="divide-y divide-white/[0.06]">
          {(log || []).map((m) => (
            <li key={m.id} className="px-5 py-3 text-sm">
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-xs text-[#6E7582]">{m.date} {m.time}</span>
                <span className="font-mono text-xs text-[#A1A8B3]">{m.session}</span>
                <span className="min-w-0 flex-1 truncate text-[#D8D2C4]">{m.turn}</span>
                <span className="font-mono text-[10px] text-[#6E7582]">{m.mode}</span>
                <span className={`rounded-full border px-2.5 py-1 font-mono text-[10px] ${FLAG[m.flag] || FLAG.none}`}>{m.flag}</span>
              </div>
              <p className="mt-1 truncate pl-1 text-xs text-[#6E7582]">↳ {m.response}</p>
            </li>
          ))}
        </ul>

        {total > 0 && (
          <div className="flex items-center justify-between gap-3 border-t border-white/[0.06] px-5 py-3">
            <span className="font-mono text-[11px] text-[#6E7582]" data-testid="moderation-range">{from}–{to} of {total}</span>
            <div className="flex gap-2">
              <button data-testid="moderation-prev-btn" onClick={() => setSkip((v) => Math.max(0, v - PAGE))} disabled={skip === 0} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-[#A1A8B3] hover:text-[#F3EFE6] disabled:opacity-30">
                <ChevronLeft size={13} /> Newer
              </button>
              <button data-testid="moderation-next-btn" onClick={() => setSkip((v) => v + PAGE)} disabled={to >= total} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-[#A1A8B3] hover:text-[#F3EFE6] disabled:opacity-30">
                Older <ChevronRight size={13} />
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
